import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';
import './onboarding.css';
import { SubcategoryService, Subcategory } from '../../services/subcategory.service';

// Imagen de placeholder para usar mientras se cargan las imágenes o si hay error
import placeholderIcon from '../../assets/images/placeholder.png'; // Asegúrate de tener esta imagen o usa cualquier otra

const CategorySelection: React.FC = () => {
  const navigate = useNavigate();
  const { updateCompanyData, setCurrentStep } = useOnboarding();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar subcategorías desde el backend
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        setLoading(true);
        const subcategories = await SubcategoryService.getAllSubcategories();
        setCategories(subcategories);
        setError(null);
      } catch (err) {
        setError('No se pudieron cargar las categorías. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, []);

  // Manejar la selección de categoría
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  // Obtener el nombre de la categoría seleccionada
  const getSelectedCategoryName = (): string | undefined => {
    if (!selectedCategory) return undefined;
    const category = categories.find(cat => cat.id === selectedCategory);
    return category?.name;
  };

  // Manejar el botón de continuar
  const handleContinue = () => {
    if (selectedCategory) {
      // Obtener el nombre de la categoría seleccionada
      const categoryName = getSelectedCategoryName();

      if (categoryName) {
        // Guardar el nombre de la categoría en el campo category para el backend
        updateCompanyData({
          category: categoryName, // Guardamos el nombre en lugar del ID
        });

        // Guardamos el ID de la categoría en localStorage para usarlo en la navegación si es necesario
        localStorage.setItem('selectedCategoryId', selectedCategory);

        setCurrentStep(2); // Avanzar al siguiente paso
        navigate('/onboarding/services'); // Navegar a la página de selección de servicios
      } else {
        console.error('No se pudo encontrar el nombre de la categoría seleccionada');
      }
    }
  };

  return (
    <OnboardingLayout
      title="Bienvenido al sistema de registro de profesional XERVIX"
      subtitle="Selecciona la categoría a la que pertenece tu empresa"
    >

      {/* Grid de categorías */}
      <div className="grid grid-cols-2 md:grid-cols-5 md:gap-4 gap-2 mb-8">
        {loading ? (
          // Mostrar esqueletos de carga mientras se cargan las categorías
          Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              className="category-card flex flex-col items-center justify-center md:p-4 p-2 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark animate-pulse"
            >
              <div className="mb-3 w-12 h-12 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
              <div className="h-4 w-20 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
          ))
        ) : error ? (
          // Mostrar mensaje de error
          <div className="col-span-full text-center py-8">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-xervix-primary text-white rounded-md hover:bg-opacity-90"
            >
              Reintentar
            </button>
          </div>
        ) : (
          // Mostrar categorías
          categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategorySelect(category.id)}
              className={`
                  category-card flex flex-col items-center justify-center md:p-4 p-2 rounded-lg transition-all
                  ${selectedCategory === category.id
                  ? 'bg-xervix-primary bg-opacity-20 border-2 border-xervix-primary'
                  : 'bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark hover:bg-opacity-80'
                }
                `}
            >
              <div className="mb-3">
                <img
                  src={category.iconUrl || placeholderIcon}
                  alt={category.name}
                  className="md:w-12 md:h-12 w-8 h-8 object-contain"
                  onError={(e) => {
                    // Si la imagen falla al cargar, usar el placeholder
                    const target = e.target as HTMLImageElement;
                    target.onerror = null; // Prevenir bucle infinito
                    target.src = placeholderIcon;
                  }}
                />
              </div>
              <span className="text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
                {category.name}
              </span>
            </button>
          ))
        )}
      </div>

      {/* Botones de acción */}
      <div className="flex justify-center gap-6">
        <CancelButton />
        <button
          onClick={handleContinue}
          disabled={!selectedCategory || loading}
          className={`
              px-10 py-3 rounded-md text-white font-medium transition-colors
              ${selectedCategory && !loading
              ? 'bg-xervix-primary hover:bg-opacity-90'
              : 'bg-gray-400 cursor-not-allowed'
            }
            `}
        >
          {loading ? 'Cargando...' : 'Continuar'}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default CategorySelection;
